import { Component, input, output } from '@angular/core';

/**
 * Reusable compare-mode toggle button with optional loading bar.
 *
 * Renders nothing when `hasPrevious` is false — the host component is responsible
 * for setting `hasPrevious` based on whether a prior revision/valve exists.
 *
 * Usage:
 * ```html
 * <app-compare-toggle-button
 *   [isCompareMode]="isCompareMode"
 *   [isLoading]="(compareLoadingIndicator$ | async) ?? false"
 *   [hasPrevious]="!!viewModel.previousVersion"
 *   (toggled)="toggleCompareMode()">
 * </app-compare-toggle-button>
 * ```
 */
@Component({
  selector: 'app-compare-toggle-button',
  templateUrl: './compare-toggle-button.component.html',
  styleUrls: ['./compare-toggle-button.component.scss'],
  standalone: false
})
export class CompareToggleButtonComponent {
  /** Whether compare mode is currently active. */
  isCompareMode = input<boolean>(false);

  /** Whether the previous version is being fetched. Disables the button and shows a progress bar. */
  isLoading = input<boolean>(false);

  /**
   * Whether a previous version is available.
   * When `false` the entire button is hidden — do not show a disabled button
   * to avoid confusing users about why comparison is not available.
   */
  hasPrevious = input<boolean>(false);

  /** Emitted when the button is clicked. The host component handles the toggle logic. */
  toggled = output<void>();
}
